import 'mapbox-gl/dist/mapbox-gl.css';

import { useMapViewState } from '@components/map/useMapViewState';
import BaseMap from '@components/ui/map/BaseMap';
import { CursorPositionTooltip } from '@components/ui/map/formMap/CursorPositionTooltip';
import DrawLayer from '@components/ui/map/formMap/DrawLayer';
import { FormMapDrawMode, FormMapProvider, useFormMapContext } from '@components/ui/map/formMap/FormMapContext';
import { MapCursorMode } from '@components/ui/map/formMap/useMapCursor';
import { MultiPolygon, Polygon } from 'geojson';
import { PropsWithChildren, RefObject, useRef } from 'react';
import { MapRef } from 'react-map-gl/mapbox';

import { Coordinates } from '@/types/commons/commons.types';
import { MapIdEnum } from '@/types/map.types';

interface FormMapContentProps {
  mapRef: RefObject<MapRef | null>;
  mapConstraintsRef: RefObject<HTMLDivElement | null>;
  hideDrawLayer: boolean;
  initialCenter?: Coordinates | null;
}

function FormMapContent({
  mapRef,
  mapConstraintsRef,
  children,
  hideDrawLayer,
  initialCenter,
}: Readonly<PropsWithChildren<FormMapContentProps>>) {
  const { state: mapViewState, setViewState } = useMapViewState(initialCenter);
  const { unit, drawMode } = useFormMapContext();

  return (
    <BaseMap
      mapId={MapIdEnum.CONFIG_EDIT}
      mapRef={mapRef}
      mapConstraintsRef={mapConstraintsRef}
      viewState={mapViewState.viewState}
      setViewState={setViewState}
    >
      {!hideDrawLayer && <DrawLayer drawMode={drawMode} />}
      <CursorPositionTooltip unit={unit} />
      {children}
    </BaseMap>
  );
}

interface FormMapProps {
  mode: MapCursorMode;
  drawMode?: FormMapDrawMode;
  polygon?: MultiPolygon | Polygon;
  hideDrawLayer?: boolean;
  initialCenter?: Coordinates | null;
}

export function FormMap({
  polygon,
  mode,
  drawMode,
  children,
  hideDrawLayer = false,
  initialCenter,
}: Readonly<PropsWithChildren<FormMapProps>>) {
  const mapRef = useRef<MapRef>(null);
  const mapConstraintsRef = useRef<HTMLDivElement>(null);

  return (
    <FormMapProvider
      mapRef={mapRef}
      mapConstraintsRef={mapConstraintsRef}
      initialGeometry={polygon ?? undefined}
      cursorMode={mode}
      drawMode={drawMode ?? 'draw_polygon'}
    >
      <FormMapContent
        initialCenter={initialCenter}
        mapRef={mapRef}
        mapConstraintsRef={mapConstraintsRef}
        hideDrawLayer={hideDrawLayer}
      >
        {children}
      </FormMapContent>
    </FormMapProvider>
  );
}
